import {ChevronRightIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {useSlots} from '@primer/react/experimental'
import Title from './Title'
import Description from './Description'
import {Button, InlineEdit, ToggleSwitch, Custom} from './Controls'
import LinkLabel from './LinkLabel'
import Footer from './Footer'
import styles from './Item.module.css'
import {clsx} from 'clsx'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export type ControlGroupItemProps = {
  children: React.ReactNode
  nestedLevel?: 0 | 1 | 2
  disabled?: boolean
  contentsClassname?: string
  /** An icon rendered inside a styled icon wrapper */
  leadingIcon?: React.ReactNode
  /** A fully custom leading visual (no icon wrapper styling) */
  leadingVisual?: React.ReactNode
  /** When provided, makes the item's title a clickable link */
  href?: string
}

const slotConfig = {
  title: Title,
  description: Description,
  custom: Custom,
  toggle: ToggleSwitch,
  button: Button,
  inlineEdit: InlineEdit,
  linkLabel: LinkLabel,
  footer: Footer,
}

function getSupplementalControl(custom: React.ReactNode, blockControl: React.ReactNode) {
  if (!custom || blockControl === custom) return null

  // Custom content is the primary control on its own but belongs below a button/toggle when both are present.
  return custom
}

const Item = ({
  children,
  nestedLevel = 0,
  disabled = false,
  contentsClassname,
  leadingIcon = null,
  leadingVisual = null,
  href,
}: ControlGroupItemProps) => {
  const controlGroupUnificationEnabled = isFeatureEnabled('control-group-unification')
  const [{title, description, custom, toggle, button, inlineEdit, linkLabel, footer}] = useSlots(children, slotConfig)

  if (controlGroupUnificationEnabled) {
    const blockControl = button || inlineEdit || custom || toggle || linkLabel
    const supplementalControl = getSupplementalControl(custom, blockControl)

    return (
      <>
        <div
          className={clsx(
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            'controlBoxContainer',
            styles.container,
            disabled && styles.disabled,
            href && styles.link,
          )}
          style={{'--nested-level': nestedLevel}}
        >
          <div className={clsx(styles.contents, contentsClassname)}>
            {leadingIcon || leadingVisual ? (
              leadingVisual ? (
                <div className={styles.leadingVisual}>{leadingVisual}</div>
              ) : (
                <div className={`${styles.leadingVisual} ${styles.blockIcon}`}>
                  <div className={styles.leadingIcon}>{leadingIcon}</div>
                </div>
              )
            ) : null}
            {href && title ? (
              <Link href={href} className={clsx(styles.title, styles.itemLink)}>
                {title}
              </Link>
            ) : (
              /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
              title && <div className={clsx(styles.title, 'titleBox')}>{title}</div>
            )}
            {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
            {description && <div className={clsx(styles.description, 'descriptionBox')}>{description}</div>}
            {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
            {blockControl && <div className={clsx(styles.blockControl, 'blockControl')}>{blockControl}</div>}
            {supplementalControl ? <div className={styles.supplementalControl}>{supplementalControl}</div> : null}
            {href && !blockControl ? (
              <div className={styles.trailingVisual}>
                {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
                <span className="d-flex fgColor-muted flex-items-center align-self-center height-full flex-justify-center">
                  <ChevronRightIcon />
                </span>
              </div>
            ) : undefined}
          </div>
        </div>
        {footer && <div className={clsx(styles.footer, 'footer')}>{footer}</div>}
      </>
    )
  } else {
    const blockControl = button || inlineEdit || custom || toggle
    const supplementalControl = getSupplementalControl(custom, blockControl)

    return (
      <div
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className={clsx('controlBoxContainer', styles.container, disabled && styles.disabled)}
        style={{'--nested-level': nestedLevel}}
      >
        <div className={clsx(styles.contents, contentsClassname)}>
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          {title && <div className={clsx(styles.title, 'titleBox')}>{title}</div>}
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          {description && <div className={clsx(styles.description, 'descriptionBox')}>{description}</div>}
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          {blockControl && <div className={clsx(styles.blockControl, 'blockControl')}>{blockControl}</div>}
          {supplementalControl ? <div className={styles.supplementalControl}>{supplementalControl}</div> : null}
        </div>
      </div>
    )
  }
}

export default Item
